import { getQueryParamValue } from "../FeatureFlag/UrlUtils";

enum HotelSearchParam {
  STAY_LENGTH = "stayLength",
  REGION_ID = "regionId",
  DAYS_IN_FUTURE = "daysInFuture",
  PWA_OVERLAY = "pwaOverlay",
  LANG_ID = "langId",
  SKIP_SERVER_FETCH = "skipServerFetch"
}

/**
 * Builds your typical Hotel-Search link using the builder pattern.
 * More info: @see https://paper.dropbox.com/doc/Blossom-Helper-Components--AvovFRbxESq0qgrtJStxY6HeAg-ph76HYqc28mEws3uEIvKc#:uid=285688582701111758947434&h2=HotelSearchLinkBuilder
 *
 * Example usage:
 * new HotelSearchLinkBuilder().withRegionId("178288").withMapOpen().build();
 *
 * This will build `/Hotel-Search?regionId=178288&pwaOveray=map`
 *
 * Note: By default, SRP uses today and tomorrow as default start/end dates.
 *
 * Also see examples in:
 * @see PropertyFiltersContext.buildLinkToSRP
 * @see useFullscreenOpen.sendUserToSRP
 * @see MapPoiSheet
 *
 */
export class HotelSearchLinkBuilder {
  private queryParams: { [key: string]: string } = {};

  public build() {
    // Feature flag
    if (getQueryParamValue("skipServerFetch") === "true") {
      this.withSkipServerFetch();
    }

    return `/Hotel-Search?${Object.entries(this.queryParams)
      .map(([key, value]) => `${key}=${value}`)
      .join("&")}`;
  }

  private addQueryParam(key: string, value: string) {
    this.queryParams[key] = value;

    return this;
  }

  public has(key: string) {
    return !!this.queryParams[key];
  }

  public withStayLength(length = 1) {
    return this.addQueryParam(HotelSearchParam.STAY_LENGTH, String(length));
  }

  // Represents a start date N days in the future from today
  public withDaysInFuture(length = 30) {
    return this.addQueryParam(HotelSearchParam.DAYS_IN_FUTURE, String(length));
  }

  public withRegionId(regionId: string) {
    return this.addQueryParam(HotelSearchParam.REGION_ID, regionId);
  }

  public withMapOpen() {
    return this.addQueryParam(HotelSearchParam.PWA_OVERLAY, "map");
  }

  public withLangId(langId: string) {
    return this.addQueryParam(HotelSearchParam.LANG_ID, langId);
  }

  // Experimental flag to skip server-side rendering on SRP
  public withSkipServerFetch() {
    return this.addQueryParam(HotelSearchParam.SKIP_SERVER_FETCH, "true");
  }

  // We assume that having multiple option values are appended using ','
  // e.g. amenities=POOL,ALL_INCLUSIVE,PET_FRIENDLY
  public withFilter(filterName: string, optionValue: string) {
    // We can only have search one region id at a time
    if (filterName === HotelSearchParam.REGION_ID) {
      return this.withRegionId(optionValue);
    }

    if (this.has(filterName)) {
      return this.addQueryParam(filterName, `${this.queryParams[filterName]},${optionValue}`);
    }

    return this.addQueryParam(filterName, optionValue);
  }
}
